import React, { memo, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Typography, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';

import MenuPopover from 'modules/views/shared/MenuPopover';
import Search from 'modules/views/shared/Search';
import SearchNew from 'modules/views/shared/SearchNew';

import DefaultAvatarSrc from 'assets/images/avatar.jpg';
import { modalGlobalStyles } from 'assets/styles';
import { ClientModel, UserModel } from 'modules/models';
import { DownloadIcon, ROUTES } from 'constants/index';
import { useHideScroll } from 'utils/useHideScroll';
import { useStyles } from './styles';
import { hasValidPermissions } from 'modules/models/user';
import Modal from 'modules/views/shared/Modal';
import { useStyles as modalStyles } from 'modules/views/shared/Modal/style';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { useHistory } from 'react-router';
import ReleaseNotes from './ReleaseNotes';

export interface IHeaderProps {
  user: UserModel.IUser;
  accountData: UserModel.IAccount;
  selfCompany: ClientModel.IClient;
  companyId: string;
  isFcaUser: boolean;
  isAdmin: boolean;
  currentUserPermissions: UserModel.IPermission[];
  isSuperAdmin?: boolean;
  isAdminOnAnyProject?: boolean;
  navigate: (path: string) => void;
  logout: () => void;
  handleDrawerToggle: () => void;
  fetchSelfClient: () => void;
}

const Header = ({
  user,
  accountData,
  selfCompany,
  companyId,
  isFcaUser,
  isAdmin,
  currentUserPermissions,
  isSuperAdmin,
  isAdminOnAnyProject,
  navigate,
  logout,
  handleDrawerToggle,
  fetchSelfClient,
}: IHeaderProps) => {
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showReleaseNoteDialog, setShowReleaseNoteDialog] = useState(false);
  const { isScrollHided, scrollWidth } = useHideScroll();
  const classes = useStyles({ scrollWidth });
  const modalClasses = modalStyles();
  const modalGlobalClasses = modalGlobalStyles();
  const [language, setLanguage] = useState('english');
  const history = useHistory();

  useEffect(() => {
    if (companyId && !selfCompany) {
      fetchSelfClient();
    }
  }, [companyId, selfCompany, fetchSelfClient]);

  const menuList = useMemo(
    () =>
      [
        { title: 'Account Settings', callback: () => navigate(ROUTES.ACCOUNT_SETTINGS.path) },
        ...(!isFcaUser
          ? [
              {
                title: 'Payment Settings',
                callback: () => navigate(ROUTES.PAYMENT_SETTINGS.path),
                permissionsExpression: UserModel.PaymentMethodsPermission.VIEWACCESS,
              },
            ]
          : []),
        ...(!isFcaUser && isAdmin ? [{ title: 'Company Tree', callback: () => navigate(ROUTES.COMPANY_TREE.path) }] : []),
        { title: 'Downloads', callback: () => navigate(ROUTES.DOWNLOADS.path) },
        { title: 'Sign Out', callback: logout },
      ].filter(menuItem => (menuItem.permissionsExpression ? hasValidPermissions(menuItem.permissionsExpression, currentUserPermissions) : true)),
    [currentUserPermissions, navigate, logout]
  );

  const profilePicture = useMemo(() => {
    if (accountData && accountData?.pictureUrl) {
      return accountData?.pictureUrl;
    }

    return DefaultAvatarSrc;
  }, [accountData]);

  const companyName = useMemo(() => {
    if (selfCompany) {
      return selfCompany.name;
    }
    return null;
  }, [selfCompany]);

  const goToDownloadsPage = () => history.push('/downloads');

  return (
    <>
      <header key="Header" data-testid="header-wrapper" className={`${classes.container} ${isScrollHided ? classes.scrollHided : ''}`}>
        <nav>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} className={classes.menuButton}>
            <MenuIcon />
          </IconButton>
          <Search />
          {/* <SearchNew /> */}
          <Box className={classes.avatarContainer}>
            <Box className={classes.emailAndRoleWrapper}>
              <Typography className={classes.avatarText}>{user.email}</Typography>
              <Typography className={`${classes.userRole} ${classes.textOverFlow}`}>
                {companyName && `${companyName} - `}
                {isSuperAdmin ? 'Super Admin' : isAdmin || isAdminOnAnyProject ? 'Admin' : 'User'}
              </Typography>
            </Box>
            <Avatar className={classes.avatarElement} alt="John Doe" src={profilePicture} />
            <span className={classes.dropdownIcon}>
              <MenuPopover divider={true} menuOptionList={menuList} placement={'bottom-end'} styleClass={classes.popoverWrapper} />
            </span>
            <Box className={classes.helpContainer} onClick={() => setShowHelpDialog(true)}>
              <span style={{ fontSize: 16, fontWeight: 600, marginRight: 10, color: '#00346B' }}>Help</span>
            </Box>
          </Box>
        </nav>
        <Modal
          show={showHelpDialog}
          styleClass={`${modalClasses.dialogContainer}`}
          onClose={() => setShowHelpDialog(true)}
          render={() => (
            <>
              <div className={classes.modalTitle}>
                <DialogTitle disableTypography={true} id="alert-dialog-title">
                  <Typography className={modalGlobalClasses.title} color="secondary" align="left" component="h1" variant="h6">
                    Help
                  </Typography>
                </DialogTitle>
                <ToggleButtonGroup color="standard" value={language} exclusive={true} onChange={(e, value) => setLanguage(value)} aria-label="Language">
                  <ToggleButton value="english">English</ToggleButton>
                  <ToggleButton value="spanish">Spanish</ToggleButton>
                </ToggleButtonGroup>
              </div>

              <DialogContent style={{ padding: '5px 13px' }}>
                <span style={{ marginRight: '3px' }}>
                  {language === 'english'
                    ? 'If you are experiencing issues in Freedom Web, you can call FCA at'
                    : 'Si tienes problemas en la página web Freedom, puedes llamar a FCA al'}{' '}
                </span>
                <span>
                  <a href="tel:+1-833-227-0637" style={{ whiteSpace: 'nowrap' }}>
                    833-227-0637
                  </a>
                </span>
                <span>{language === 'english' ? ' Monday through Friday from 6 AM to 6 PM CST' : ' de lunes a viernes de 6 a.m. a 6 p.m. CST'}</span>
                <span style={{ marginRight: '3px' }}>{language === 'english' ? ', email us at' : ', enviarnos un correo electrónico a'} </span>
                <span>
                  <a href="mailto:customer.support@fieldca.com" style={{ whiteSpace: 'nowrap' }}>
                    customer.support@fieldca.com
                  </a>
                </span>
                <span style={{ marginRight: '3px' }}>
                  {language === 'english' ? ', or report an issue at' : ', o reportar un problema en el sitio web siguiente'}{' '}
                </span>
                <span>
                  <a href="https://fccs.atlassian.net/servicedesk/customer/portal/12" target="_blank" rel="noopener noreferrer">
                    https://fccs.atlassian.net/servicedesk/customer/portal/12
                  </a>
                </span>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowHelpDialog(false)} color="primary" data-testid="assign-btn-close">
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowHelpDialog(false);
                    setShowReleaseNoteDialog(true);
                  }}
                  color="primary"
                  data-testid="assign-btn-close"
                >
                  Release Notes
                </Button>
              </DialogActions>
            </>
          )}
        />
      </header>
      {showReleaseNoteDialog ? (
        <ReleaseNotes
          show={showReleaseNoteDialog}
          onClose={() => {
            setShowHelpDialog(true);
            setShowReleaseNoteDialog(false);
          }}
        />
      ) : null}
    </>
  );
};

export default memo(Header);
